import React from 'react';
import { SEO } from '@/components/SEO';
import { Phone, CheckCircle, Star, MapPin, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ContactFormSimple from '@/components/ContactFormSimple';
import Navbar from '@/components/Navbar';

const FreeEstimatePage = () => {
  return (
    <>
      <SEO
        title="Free Power Washing Estimate | Ventura County | Ventura Power Washing"
        description="Get your free power washing estimate in Ventura County. Professional pressure washing, window cleaning & more. Call (805) 464-7166 for instant quote!"
        keywords="free estimate, power washing quote, pressure washing Ventura County, window cleaning estimate"
        path="/free-estimate"
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Free Power Washing Estimate",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Ventura Power Washing",
              "telephone": "(805) 464-7166",
              "address": {
                "@type": "PostalAddress",
                "addressRegion": "CA",
                "addressLocality": "Ventura County"
              }
            },
            "areaServed": [
              "Camarillo", "Fillmore", "Moorpark", "Oxnard", "Port Hueneme", 
              "Santa Paula", "Simi Valley", "Thousand Oaks", "Ventura"
            ],
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "description": "Free estimate for power washing services"
            }
          })}
        </script>
      </SEO>

      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-ventura-blue to-ventura-darkBlue text-white py-16">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Get Your FREE Power Washing Estimate
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
            Professional cleaning services in Ventura County. No obligation, fast response within 24 hours!
          </p>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <span>Licensed & Insured</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-400" />
              <span>5-Star Rated</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-300" />
              <span>Ventura County Local</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Highlight */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl font-bold text-center mb-8">Our Professional Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              'Pressure Washing',
              'Window Cleaning',
              'Carpet Cleaning',
              'Soft Wash Exterior',
              'Gutter Cleaning',
              'Solar Panel Cleaning',
              'Dumpster Pad Cleaning'
            ].map((service) => (
              <Card key={service} className="text-center">
                <CardContent className="p-4">
                  <CheckCircle className="h-8 w-8 text-ventura-blue mx-auto mb-2" />
                  <p className="font-medium text-sm">{service}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-12" role="main">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl font-bold text-center mb-8">Get a Free Estimate</h2>
          <p className="text-xl text-center mb-12 text-gray-600 max-w-2xl mx-auto">
            Fill out the form below and we'll contact you promptly with a free, no-obligation estimate
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <ContactFormSimple />
            </div>
            
            {/* Contact Information & Why Choose Us */}
            <div className="space-y-6">
              {/* Contact Info */}
              <Card className="card-shadow bg-ventura-blue text-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-6">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Phone className="h-5 w-5 mr-3 mt-1" />
                      <div>
                        <p className="font-medium">Phone</p>
                        <a href="tel:+18054647166" className="text-white hover:underline" aria-label="Call Ventura Power Washing at 805-464-7166">
                          (805) 464-7166
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <MapPin className="h-5 w-5 mr-3 mt-1" />
                      <div>
                        <p className="font-medium">Service Areas</p>
                        <p className="text-sm text-white/90">
                          Camarillo, Fillmore, Moorpark, Oxnard, Port Hueneme, Santa Paula, Simi Valley, Thousand Oaks, and Ventura
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-white/20">
                    <p className="font-medium mb-2">Business Hours</p>
                    <div className="text-sm space-y-1">
                      <p>Monday - Saturday: 7AM - 7PM</p>
                      <p>Sunday: Available for emergencies</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Why Choose Us */}
              <Card className="card-shadow">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4">Why Choose Us?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-ventura-blue mr-3 text-lg">✓</span>
                      <span className="text-sm">Licensed & Insured</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-ventura-blue mr-3 text-lg">✓</span>
                      <span className="text-sm">Free Estimates</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-ventura-blue mr-3 text-lg">✓</span>
                      <span className="text-sm">Satisfaction Guaranteed</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-ventura-blue mr-3 text-lg">✓</span>
                      <span className="text-sm">Eco-Friendly Solutions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-ventura-blue mr-3 text-lg">✓</span>
                      <span className="text-sm">Local Family Business</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-ventura-blue mr-3 text-lg">✓</span>
                      <span className="text-sm">Same-Day Service Available</span>
                    </li>
                  </ul>
                  
                  <Button 
                    className="w-full mt-6 button-primary"
                    onClick={() => window.location.href = "tel:+18054647166"}
                    aria-label="Call Ventura Power Washing now at 805-464-7166"
                  >
                    Call Now: (805) 464-7166
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Minimal Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <div className="inline-block bg-white rounded-lg px-4 py-2 mb-4">
            <img
              src="/logo/ventura-power-washing-horizontal.png"
              alt="Ventura Power Washing"
              width="900"
              height="330"
              className="h-12 w-auto"
            />
          </div>
          <p className="text-gray-400 mb-2">
            Professional cleaning services throughout Ventura County
          </p>
          <p className="text-gray-400">
            <a href="tel:+18054647166" className="hover:text-white transition-colors" aria-label="Call Ventura Power Washing at 805-464-7166">
              (805) 464-7166
            </a>
          </p>
          <p className="text-gray-500 text-sm mt-4">
            © {new Date().getFullYear()} Ventura Power Washing. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  );
};

export default FreeEstimatePage;